import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import { productsAPI, categoriesAPI } from '../../services/api';
import {
  ModernInput,
  ModernSelect,
  ModernButton,
  ModernModal
} from '../ui/ModernComponents';
import toast from 'react-hot-toast';

const AddProductForm = ({ onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    name: '',
    brand: '',
    model: '',
    purchase_price: '',
    sale_price: '',
    stock: '0',
    category_id: '',
    barcode: '',
    // Lens fields
    eye_side: '',
    sph: '',
    cyl: '',
    axis: '',
    addition: '',
    diameter: '',
    material: '',
    coating: '',
    index: '',
    tint: '',
    // Frame fields
    frame_size: '',
    bridge_size: '',
    temple_length: '',
    frame_material: '',
    frame_shape: '',
    frame_color: '',
    achat_facture: 'facture',
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  const handleChange = (e) => {
    // Handle regular input events
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    
    try {
      const response = await productsAPI.createProduct(form);
      toast.success('Produit ajouté avec succès');
      onSuccess(response.data);
    } catch (error) {
      console.error('Error creating product:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(`Erreur: ${errorMessages}`);
      } else {
        toast.error('Erreur lors de la création du produit');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  // Determine if we should show lens or frame fields based on category
  const selectedCategory = categories.find(c => c.id.toString() === form.category_id);
  const showLensFields = selectedCategory?.name?.toLowerCase().includes('verre');
  const showFrameFields = selectedCategory?.name?.toLowerCase().includes('monture');

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Ajouter un nouveau produit</h3>
        <p className="text-sm text-gray-600">Remplissez les informations du produit</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ModernInput
            label="Nom du produit"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ex: Lunettes de vue classiques"
            required
          />
          
          <ModernInput
            label="Marque"
            name="brand"
            value={form.brand}
            onChange={handleChange}
            placeholder="Ex: Ray-Ban"
          />
          
          <ModernInput
            label="Modèle"
            name="model"
            value={form.model}
            onChange={handleChange}
            placeholder="Ex: RB3025"
          />
          
          <ModernInput
            label="Prix d'achat"
            name="purchase_price"
            type="number"
            min="0"
            step="0.01"
            value={form.purchase_price}
            onChange={handleChange}
            placeholder="0.00"
            required
          />
          
          <ModernInput
            label="Prix de vente"
            name="sale_price"
            type="number"
            min="0"
            step="0.01"
            value={form.sale_price}
            onChange={handleChange}
            placeholder="0.00"
            required
          />
          
          <ModernInput
            label="Stock"
            name="stock"
            type="number"
            min="0"
            value={form.stock}
            onChange={handleChange}
            placeholder="0"
            required
          />
          
          <ModernSelect
            label="Catégorie"
            name="category_id"
            value={categories.map(c => ({ value: c.id.toString(), label: c.name })).find(opt => opt.value === form.category_id) || null}
            onChange={(option) => setForm(prev => ({ ...prev, category_id: option ? option.value : '' }))}
            options={categories.map(c => ({ value: c.id.toString(), label: c.name }))}
            placeholder="Sélectionner une catégorie"
            required
          />

          <ModernSelect
            label="Facture d'achat"
            value={[
              { value: 'facture', label: 'Avec facture achat' },
              { value: 'non_facture', label: 'Sans facture achat (ex. noir)' },
            ].find(opt => opt.value === (form.achat_facture || 'facture')) || null}
            onChange={(option) => setForm(prev => ({ ...prev, achat_facture: option ? option.value : 'facture' }))}
            options={[
              { value: 'facture', label: 'Avec facture achat' },
              { value: 'non_facture', label: 'Sans facture achat (ex. noir)' },
            ]}
          />
          
          <ModernInput
            label="Code-barres"
            name="barcode"
            value={form.barcode}
            onChange={handleChange}
            placeholder="Ex: 1234567890123"
          />
        </div>

        {/* Lens Fields - shown conditionally */}
        {showLensFields && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-blue-700 mb-3">Détails Verres</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ModernSelect
                label="Côté de l'œil"
                name="eye_side"
                value={[
                  { value: '', label: '---' },
                  { value: 'left', label: 'Gauche' },
                  { value: 'right', label: 'Droit' },
                  { value: 'both', label: 'Les deux' }
                ].find(opt => opt.value === form.eye_side) || null}
                onChange={(option) => setForm(prev => ({ ...prev, eye_side: option ? option.value : '' }))}
                options={[
                  { value: '', label: '---' },
                  { value: 'left', label: 'Gauche' },
                  { value: 'right', label: 'Droit' },
                  { value: 'both', label: 'Les deux' }
                ]}
              />
              <ModernInput label="Sphère (Sph)" name="sph" value={form.sph} onChange={handleChange} placeholder="Ex: -2.00" type="number" step="0.01" />
              <ModernInput label="Cylindre (Cyl)" name="cyl" value={form.cyl} onChange={handleChange} placeholder="Ex: -0.75" type="number" step="0.01" />
              <ModernInput label="Axe" name="axis" value={form.axis} onChange={handleChange} placeholder="Ex: 90" type="number" />
              <ModernInput label="Addition" name="addition" value={form.addition} onChange={handleChange} placeholder="Ex: 2.50" type="number" step="0.01" />
              <ModernInput label="Diamètre (mm)" name="diameter" value={form.diameter} onChange={handleChange} placeholder="Ex: 70" type="number" />
              <ModernInput label="Matériau" name="material" value={form.material} onChange={handleChange} placeholder="Ex: Polycarbonate" />
              <ModernInput label="Traitement" name="coating" value={form.coating} onChange={handleChange} placeholder="Ex: Anti-reflet" />
              <ModernInput label="Indice" name="index" value={form.index} onChange={handleChange} placeholder="Ex: 1.6" />
              <ModernInput label="Teinte" name="tint" value={form.tint} onChange={handleChange} placeholder="Ex: Brun" />
            </div>
          </div>
        )}

        {/* Frame Fields - shown conditionally */}
        {showFrameFields && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-green-700 mb-3">Détails Monture</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ModernInput label="Taille de la monture" name="frame_size" value={form.frame_size} onChange={handleChange} placeholder="Ex: 54" />
              <ModernInput label="Taille du pont" name="bridge_size" value={form.bridge_size} onChange={handleChange} placeholder="Ex: 18" />
              <ModernInput label="Longueur des branches" name="temple_length" value={form.temple_length} onChange={handleChange} placeholder="Ex: 140" />
              <ModernInput label="Matériau monture" name="frame_material" value={form.frame_material} onChange={handleChange} placeholder="Ex: Acétate" />
              <ModernInput label="Forme monture" name="frame_shape" value={form.frame_shape} onChange={handleChange} placeholder="Ex: Carrée" />
              <ModernInput label="Couleur monture" name="frame_color" value={form.frame_color} onChange={handleChange} placeholder="Ex: Noir" />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t">
          <ModernButton
            type="button"
            variant="secondary"
            onClick={handleCancel}
            icon={<X className="w-4 h-4" />}
          >
            Annuler
          </ModernButton>
          <ModernButton
            type="submit"
            variant="primary"
            icon={<Save className="w-4 h-4" />}
            disabled={submitLoading}
          >
            {submitLoading ? 'Création...' : 'Créer le produit'}
          </ModernButton>
        </div>
      </form>
    </div>
  );
};

export default AddProductForm;
